/**
 * Zone Detail Component
 *
 * Displays detailed information for a single zone with tabs for:
 * - Hosts (ESXi/OpenNebula hosts)
 * - Datastores (storage)
 * - Instances (VMs)
 */

import SubHeaders from '@/components/Headers/SubHeaders'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getZoneCapacity } from '../../../services/capacity'
import { paths } from '@/utils/path'
import { useParams } from 'react-router-dom'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { GridInformation, Tabs } from 'vnetwork-ui'
import { Badge } from '@/components/ui/badge'
import useTabs from '@/hooks/useTabs'
import { tabs } from './tabs'
import type { ZoneStatus } from '../../../types'

/** Get status badge variant based on zone status */
const getStatusBadgeVariant = (status: ZoneStatus) => {
    switch (status) {
        case 'healthy':
            return 'default'
        case 'degraded':
            return 'secondary'
        case 'error':
            return 'destructive'
        default:
            return 'outline'
    }
}

/** Get status badge class for coloring */
const getStatusBadgeClass = (status: ZoneStatus) => {
    switch (status) {
        case 'healthy':
            return 'bg-green-100 text-green-700 border-green-200'
        case 'degraded':
            return 'bg-amber-100 text-amber-700 border-amber-200'
        case 'error':
            return 'bg-red-100 text-red-700 border-red-200'
        default:
            return ''
    }
}

const ZoneDetail = () => {
    const { zoneId } = useParams<{ zoneId: string }>()

    const { data: zone, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.CAPACITY_ZONE, zoneId],
        queryFn: () => getZoneCapacity(zoneId!),
    })

    const { TabComponent, TabProps } = useTabs({ tabs: tabs(zone) })

    // Render loading/error state at the end (after all hooks)
    if (renderState()) return renderState()

    const hostCount = zone?.compute.hosts?.length || 0
    const vmCount =
        zone?.compute.hosts?.reduce((sum, h) => sum + h.vm_count, 0) || 0

    return (
        <div>
            <SubHeaders
                actions={[]}
                breadcrumbs={[
                    {
                        path: paths.warehouse,
                        title: 'Capacities & Resources',
                    },
                    {
                        path: `${paths.warehouse}?tab=cloud`,
                        title: 'Cloud Resources',
                    },
                    {
                        path: '',
                        title: zone?.zone_name || '',
                    },
                ]}
            />
            <GridInformation
                data={[
                    {
                        label: 'Zone Name',
                        value: zone?.zone_name || '-',
                    },
                    {
                        label: 'Provider',
                        value: zone?.provider_name || '-',
                    },
                    {
                        label: 'Provider Type',
                        value: (
                            <Badge variant="outline">
                                {zone?.provider_type?.toUpperCase() || ''}
                            </Badge>
                        ),
                    },
                    {
                        label: 'Status',
                        value: (
                            <Badge
                                variant={getStatusBadgeVariant(
                                    zone?.status || 'healthy'
                                )}
                                className={getStatusBadgeClass(
                                    zone?.status || 'healthy'
                                )}
                            >
                                {zone?.status?.toUpperCase() || 'UNKNOWN'}
                            </Badge>
                        ),
                    },
                    {
                        label: 'Total Hosts',
                        value: hostCount.toString(),
                    },
                    {
                        label: 'Total VMs',
                        value: vmCount.toString(),
                    },
                    {
                        label: 'CPU Usage',
                        value: `${zone?.compute.avg_cpu_usage_pct?.toFixed(1) || 0}%`,
                    },
                    {
                        label: 'Memory Usage',
                        value: `${zone?.compute.avg_memory_usage_pct?.toFixed(1) || 0}%`,
                    },
                    {
                        label: 'Storage Usage',
                        value: `${zone?.storage.usage_pct?.toFixed(1) || 0}%`,
                    },
                    {
                        label: 'Storage Capacity',
                        value: `${zone?.storage.total_capacity_gb || 0} GB`,
                    },
                    {
                        label: 'Error',
                        value: zone?.error || '-',
                        className: 'lg:col-span-2 text-red-600',
                        hidden: !zone?.error,
                    },
                ]}
                head={{
                    title: 'Zone Information',
                }}
            />

            <Tabs {...TabProps} />

            {TabComponent}
        </div>
    )
}

export default ZoneDetail
